import { useContext, useEffect, type ComponentType } from 'react';
import _ from 'lodash';
import { useOutletContext, useParams } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useGetSerpDataQuery } from '@/app/Serp/api/getSerpDataApi';
import { Col, Row } from 'react-bootstrap';
import { ResultsConfig } from '@/app/Serp/config/columnConfig';
import { serpTooltips } from '@/app/Serp/tooltip/serpTooltips';
import { useSelector, useDispatch } from 'react-redux';
import { setSerp } from '@/app/Serp/reducers/serpDataSlice';
import { useGetStatusSerpQuery } from '@/app/Serp/api/historyApi';
import NoSerpData from '../components/common/NoSerpData';
import CharacteristicDistributionOfTechs from '../components/results/CharacteristicDistributionOfTechs';
import CircularChartCard from '../components/CircularChartCard';
import SpamScore from '../components/results/SpamScore';
import DomainAuthoritySpamScore from '../components/results/DomainAuthoritySpamScore';
import DomainAuthorityHistory from '../components/results/DomainAuthorityHistory';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import CardStackedBarChartLoading from '../../common/components/skeleton/CardStackedBarChartLoading';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import DomainAgeAndRank from '../components/results/DomainAgeAndRank';
import DomainRanksWords from '../components/results/DomainRanksWords';
import CardChartCircleLoading from '@/app/common/components/skeleton/CardChartCircleLoading';
import CardBarChartLoading from '@/app/common/components/skeleton/CardBarChartLoading';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';
import { NewSerpContext } from '@/context/Context';
import useDomain from '@/hooks/useDomain';
import { getColor } from '@/helpers/utils';
import type { SerpDataRootState } from '@/app/Serp/types/serpApiTypes';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type AgGridWrapperProps = {
  height: string;
  columns: unknown[];
  data: unknown[] | undefined;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

const Results = () => {
  const dispatch = useDispatch();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const { step } = useContext(NewSerpContext);
  const serpData = useSelector((state: SerpDataRootState) => state.serpData);

  useEffect(() => {
    setBreadcrumbData([{ text: 'بررسی نتایج', href: '#' }]);
  }, []);

  const { slug } = useParams<{ slug?: string }>();
  const { domain_slug } = useDomain();
  const { data: userInfo } = useGetUserInfoQuery();
  const serp_id = slug === 'last' ? 'last' : slug;

  useGetStatusSerpQuery({
    serp_id: serp_id || 'last',
    domain_slug,
    requested_user: {
      name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
      email: userInfo?.data?.email
    }
  });

  const { data, isLoading, isError, error, refetch, isFetching } =
    useGetSerpDataQuery(
      {
        serp_id: serp_id ?? 'last',
        domain_slug,
        requested_user: {
          name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
          email: userInfo?.data?.email
        }
      },
      {
        skip: step !== 1
      }
    );

  useEffect(() => {
    if (!data) return;
    dispatch(setSerp(data.data ?? null));
  }, [data]);

  const colorMaker = (index: number) => {
    if (index % 5 === 0) {
      return 'primary';
    } else if (index % 4 === 0) {
      return 'info';
    } else if (index % 3 === 0) {
      return 'success';
    } else if (index % 2 === 0) {
      return 'warning';
    } else if (index % 1 === 0) {
      return 'danger';
    } else {
      return 'warning';
    }
  };

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }
  if (!isLoading && Object.keys(serpData).length == 0) return <NoSerpData />;

  return (
    <Row className="g-3 mb-3">
      <Col xl={5} md={12}>
        {!isLoading && serpData?.analysis_serp?.results_types ? (
          <CircularChartCard
            title={<> توزیع ویژگی نتایج</>}
            tooltip={serpTooltips.characteristicDistributionOfResults}
            footer={
              <>
                در نتایج تبلیغات{' '}
                {serpData.analysis_serp.results_types.filter(
                  item => item.name == 'تبلیغات'
                ).length != 0 ? (
                  <>وجود دارد</>
                ) : (
                  <>وجود ندارد</>
                )}{' '}
                و فیچر اسنیپت{' '}
                {serpData.analysis_serp.results_types.filter(
                  item => item.name == 'فیچر اسنیپت'
                ).length != 0 ? (
                  <>وجود دارد</>
                ) : (
                  <>وجود ندارد</>
                )}
              </>
            }
            data={serpData.analysis_serp.results_types.map((item, i) => ({
              color: colorMaker(i),
              count: item.y,
              percentage: item.y,
              name: item.name
            }))}
          />
        ) : (
          <CardChartCircleLoading
            title="توزیع ویژگی نتایج"
            tooltip={serpTooltips.characteristicDistributionOfResults}
          />
        )}
      </Col>

      <Col xl={7} md={12}>
        {!isLoading && serpData?.analysis_domains?.tech_domains ? (
          <CharacteristicDistributionOfTechs
            data={serpData.analysis_domains.tech_domains}
          />
        ) : (
          <CardStackedBarChartLoading
            title={<>توزیع تکنولوژی سایت‌ها </>}
            tooltip={serpTooltips.characteristicDistributionOfResults}
          />
        )}
      </Col>

      <Col xxl={4} md={6}>
        {!isLoading && serpData?.analysis_serp?.num_raiting !== undefined ? (
          <CircularChartCard
            title={<>سایت های دارای Rating </>}
            tooltip={serpTooltips.ratingChart}
            footer={
              <>
                {serpData.analysis_serp.num_raiting === 0 ? (
                  <>
                    هیچ سایتی Rating نداره، و این فرصت خوبیه برای استفاده از
                    Rating
                  </>
                ) : (serpData.analysis_serp.num_raiting ?? 0) < 5 ? (
                  <>سایت‌های کمی Rating دارن، برای بهتر دیده شدن استفاده کن</>
                ) : (
                  <>اکثر سایت‌ها Rating دارن و بهتره شما هم استفاده کنی</>
                )}
              </>
            }
            data={[
              {
                color: getColor('primary'),
                percentage: serpData.analysis_serp.num_raiting,
                count: serpData.analysis_serp.num_raiting,
                name: 'دارای rating'
              },
              {
                color: 'info',
                percentage:
                  (serpData.analysis_serp.num_organic ?? 0) -
                  (serpData.analysis_serp.num_raiting ?? 0),
                count:
                  (serpData.analysis_serp.num_organic ?? 0) -
                  (serpData.analysis_serp.num_raiting ?? 0),
                name: 'بدون rating'
              }
            ]}
          />
        ) : (
          <CardChartCircleLoading
            title={<>سایت های دارای Rating </>}
            tooltip={serpTooltips.ratingChart}
          />
        )}
      </Col>

      <Col xxl={4} md={6}>
        {!isLoading &&
        (serpData?.analysis_domains?.num_ir_host ||
          serpData?.analysis_domains?.num_non_ir_host) ? (
          <CircularChartCard
            title={<>توزیع مکان هاست</>}
            tooltip={serpTooltips.distributionOfHostLocation}
            footer={
              <>
                {(serpData.analysis_domains.num_ir_host ?? 0) >
                (serpData.analysis_domains.num_non_ir_host ?? 0) ? (
                  <>
                    <b>
                      {Math.round(
                        ((serpData.analysis_domains.num_ir_host ?? 0) * 100) /
                          ((serpData.analysis_domains.num_ir_host ?? 0) +
                            (serpData.analysis_domains.num_non_ir_host ?? 0))
                      )}{' '}
                    </b>
                    درصد سایت ها از هاست داخل ایران استفاده میکنند.
                  </>
                ) : (
                  <>
                    <b>
                      {Math.round(
                        ((serpData.analysis_domains.num_non_ir_host ?? 0) *
                          100) /
                          ((serpData.analysis_domains.num_ir_host ?? 0) +
                            (serpData.analysis_domains.num_non_ir_host ?? 0))
                      )}{' '}
                    </b>
                    درصد سایت ها از هاست خارج ایران استفاده میکنند.
                  </>
                )}
              </>
            }
            data={[
              {
                color: getColor('primary'),
                percentage: serpData.analysis_domains.num_ir_host,
                count: serpData.analysis_domains.num_ir_host ?? 0,
                name: 'داخل ایران'
              },
              {
                color: 'info',
                percentage: serpData.analysis_domains.num_non_ir_host,
                count: serpData.analysis_domains.num_non_ir_host ?? 0,
                name: 'خارج ایران'
              }
            ]}
          />
        ) : (
          <CardChartCircleLoading
            title={<>توزیع توزیع مکان هاست</>}
            tooltip={serpTooltips.distributionOfHostLocation}
          />
        )}
      </Col>

      <Col xxl={4} md={12}>
        {!isLoading && serpData?.analysis_domains?.domains_suffix ? (
          <CircularChartCard
            title={<>توزیع پسوند دامنه</>}
            tooltip={serpTooltips.distributionOfDomainSuffix}
            footer={
              <span>
                <span> اکثر سایت ها دارای دامنه با پسوند </span>
                <b>
                  {
                    _.maxBy(
                      serpData.analysis_domains.domains_suffix,
                      item => item.y
                    )?.name
                  }
                </b>{' '}
                هستند.
              </span>
            }
            data={serpData.analysis_domains.domains_suffix.map((item, i) => ({
              color: colorMaker(i),
              count: item.y,
              percentage: item.y,
              name: item.name
            }))}
          />
        ) : (
          <CardChartCircleLoading
            title={<>توزیع پسوند دامنه</>}
            tooltip={serpTooltips.distributionOfDomainSuffix}
          />
        )}
      </Col>

      <Col xl={6} md={12}>
        {!isLoading && serpData?.analysis_domains ? (
          <DomainAuthoritySpamScore
            data={serpData?.analysis_domains.spam_authority_visits}
          />
        ) : (
          <CardBarChartLoading
            title="اسپم اسکور و دامین آتوریتی و بازدید سایت"
            tooltip={serpTooltips.spamAuthorityVisit}
            limit={13}
            width={20}
            height={160}
            margin={20}
            marginBetweenLine={8}
          />
        )}
      </Col>
      <Col xl={6} md={12}>
        {!isLoading && serpData?.analysis_domains ? (
          <SpamScore data={serpData.analysis_domains.domains_spam_score} />
        ) : (
          <CardBarChartLoading
            title="اسپم اسکور "
            tooltip={serpTooltips.spamScore}
            limit={13}
            width={20}
            height={160}
            margin={20}
            marginBetweenLine={8}
          />
        )}
      </Col>

      <Col xl={12} md={12}>
        {!isLoading && serpData?.analysis_domains?.domains_moz ? (
          <DomainAuthorityHistory
            data={serpData?.analysis_domains.domains_moz}
          />
        ) : (
          <CardBarChartLoading
            title="تاریخچه دامین آتوریتی"
            tooltip={serpTooltips.domainAuthority}
            limit={25}
            width={20}
            height={150}
            margin={20}
            marginBetweenLine={10}
          />
        )}
      </Col>

      <Col xl={12} md={12}>
        {!isLoading ? (
          <TypedAgGridWrapper
            height="526px"
            columns={ResultsConfig}
            data={serpData.items}
          />
        ) : (
          <AgGridSkeleton height="576px" />
        )}
      </Col>
      <Col xl={6} md={12}>
        {!isLoading && serpData?.analysis_domains?.domains_ranks_age ? (
          <DomainAgeAndRank
            data={serpData.analysis_domains.domains_ranks_age}
          />
        ) : (
          <CardBarChartLoading
            title="رتبه و سن دامنه"
            tooltip={serpTooltips.domainAgeNRank}
            limit={25}
            width={10}
            height={0}
            margin={0}
            marginBetweenLine={5}
          />
        )}
      </Col>
      <Col xl={6} md={12}>
        {!isLoading && serpData.analysis_domains?.domains_ranks_words ? (
          <DomainRanksWords
            data={serpData.analysis_domains.domains_ranks_words}
          />
        ) : (
          <CardBarChartLoading
            title="رتبه و تعداد کلمات"
            tooltip={serpTooltips.domainRanksWords}
            limit={25}
            width={10}
            height={0}
            margin={0}
            marginBetweenLine={5}
          />
        )}
      </Col>
    </Row>
  );
};

export default Results;
